const BasePlugin = require('./basePlugin');
const { processGeoJSON, isValidLocation, createTempSvgElement, isLocationClass } = require('../mapUtils');
const TextMarker = require('./textMarker');
const FittedTextMarker = require('./fittedTextMarker');
const OffsetLabel = require('./offsetLabel');
const QuickviewPopup = require('./quickviewPopup');
const { getPoleOfInaccessibility } = require('../mapUtils');
const edit = require('../../layout/components/edit');

const ZoneMarker = function(layer, locationRecord) {
  // Get the center of the layer
  let layerCenter = getPoleOfInaccessibility(layer, 0.00001);
  if (Array.isArray(layerCenter)) {
    layerCenter = { lat: layerCenter[1], lng: layerCenter[0] };
  }
  
  if (!layerCenter) {
    layerCenter = layer.getBounds().getCenter();
  }

  // Create the SVG element for the text overlay
  const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  
  // **Disable pointer events on the entire SVG**
  svgElement.style.pointerEvents = 'none'; // Ensures the SVG doesn't capture any mouse events

  // Set a fixed viewBox size to keep text size consistent
  svgElement.setAttribute('viewBox', '0 0 260 100'); // Adjust dimensions as needed

  // **Option A: Remove the transparent rect and only include text elements**
  if (locationRecord.roomNumber) {
    svgElement.innerHTML = `
      <rect width="400" height="100" fill="#ffffff" opacity="0.0"/>
      <text x="200" y="30" style="font-size:18px; text-anchor: middle; fill: #000000">
        ${locationRecord.roomNumber}
      </text>
      <text x="200" y="70" style="font-size:14px; text-anchor: middle; fill: #000000">
        ${locationRecord.name}
      </text>
    `;
  } else {
    // Center single-line text vertically for thermostats or other single-label records
    svgElement.innerHTML = `
      <rect width="400" height="100" fill="#ffffff" opacity="0.0"/>
      <text x="200" y="52" style="font-size:18px; text-anchor: middle; dominant-baseline: middle; fill: #000000">
        ${locationRecord.name}
      </text>
    `;
  }

  // Define a small, fixed-sized bounding box for the SVG overlay
  const svgElementBounds = [
    [layerCenter.lat + 0.00001, layerCenter.lng + 0.00004], // Top-right corner
    [layerCenter.lat - 0.00001, layerCenter.lng - 0.00004]  // Bottom-left corner
  ];

  // Create the SVG overlay with fixed bounds and non-interactive
  const svgOverlay = L.svgOverlay(svgElement, svgElementBounds, { interactive: false });

  return svgOverlay;
};


class EquipmentLayerPlugin extends BasePlugin {
  constructor(ctrl) {
    super(ctrl);

    const getLayersForEquipment = function getLayersForEquipment(contextRecord, locationEdge) {
      const enableEditing = contextRecord.name == 'this';
      const equipmentRecord = locationEdge.in ? locationEdge.in : locationEdge;

      let equipmentStyle = {
        color: '#000000',
        weight: 2, // Line width
        opacity: 1, // Line opacity
        fillColor: equipmentRecord.mapPolygonFillColor || '#CCCCCC', // Fill color
        fillOpacity: 0.7, // Fill opacity
      };

      // Emphasize the currently focused equipment and its component children
      if (ctrl.focusRid && (equipmentRecord['@rid'] === ctrl.focusRid || equipmentRecord.equipmentParentRid === ctrl.focusRid)) {
        Object.assign(equipmentStyle, {
          weight: 4,
          color: '#FFFFFF',
          dashArray: "10,10",
        });
      }

      if (isLocationClass(equipmentRecord['@class'])) return [];
      // Do not render Zone classes here; zones are handled by zoneLayers
      if (['Zone'].some(term => equipmentRecord['@class'].includes(term))) return [];
      if (!isValidLocation(equipmentRecord)) return;
      const locationId = equipmentRecord['@rid'].toString().slice(1);
      // Let's try creating an SVG overlay instead of a GeoJSON layer
      let equipmentLayer = processGeoJSON(equipmentRecord, locationId, enableEditing);

      if (equipmentLayer && equipmentLayer.getLayers) {
        equipmentLayer.getLayers().forEach(l => {
          if (l && l.options) {
            l.options.pane = 'pane-equipment';
            if (l.redraw) l.redraw();
          }
        });
      } else if (equipmentLayer && equipmentLayer.options) {
        equipmentLayer.options.pane = 'pane-equipment';
      }

      if (equipmentRecord && equipmentRecord.mapSvg) {
        let svgElement = createTempSvgElement(equipmentRecord.mapSvg);
        const overlay = L.svgOverlay(svgElement, equipmentLayer.getBounds(), { pane: 'pane-equipment' });
        equipmentLayer = overlay;
      }

      if (!equipmentLayer) return;
      // Tag layer with its context so edit plugin can find it
      try { equipmentLayer.context = contextRecord.name; } catch (e) {}
      if (equipmentLayer?.getLayers) {
        equipmentLayer.getLayers().forEach(feature => {
          if (feature?.feature?.properties?.name !== 'Bounding_Box') {
            feature.setStyle(equipmentStyle);
          }
        });
      }

      return equipmentLayer;
    };

    const isActiveEquipment = (equipmentRecord) => {
      if (!ctrl?.equipmentRecord || !ctrl.equipmentRecord()) return false;
      const activeRid = ctrl.equipmentRecord()['@rid'];
      const recordRid = equipmentRecord && equipmentRecord['@rid'];
      return Boolean(activeRid && recordRid && activeRid.toString() === recordRid.toString());
    };

    const handleFeatureAdditions = async function handleFeatureAdditions(features) {
      const isOverview = ctrl.mapSelectedView() == 'Overview';
      const includeArr = typeof ctrl.alwaysIncludeRids === 'function'
        ? (ctrl.alwaysIncludeRids() || [])
        : (ctrl.alwaysIncludeRids || []);
      const alwaysInclude = new Set(includeArr);
      for (const feature of features) {
        let equipmentRecord = feature.in ? feature.in : feature;
        if (isLocationClass(equipmentRecord['@class'])) continue;  // Skip location classes
        // In views like 'Overview', only render exceptions explicitly allowed
        if (isOverview && !alwaysInclude.has(equipmentRecord['@rid'])) continue;
        let contextRecord = ctrl.contexts.find(c => c.name === feature.context);
        if (edit.vm.editing() && isActiveEquipment(equipmentRecord)) continue;

        let equipmentLayer = getLayersForEquipment(contextRecord, equipmentRecord);
        if (!equipmentLayer || equipmentLayer?.length == 0) continue  ;
        equipmentLayer.UID = feature.UID;
        equipmentLayer.connectionName = equipmentRecord['@class'] + ' - ' + equipmentRecord.name;
        try {
          // Add new layer to the map
          ctrl.allLayers.push(equipmentLayer);
          equipmentLayer.addTo(ctrl.equipmentParentLayer);
          // Place markers on leaf-level polygons unless explicitly hidden
          const hideLabel = equipmentRecord.hideLabel === true
            || equipmentRecord.hideLabel === 'true'
            || equipmentRecord.hideLabel === 1;
          let markers = [];
          if (!hideLabel) {
            if (equipmentRecord['@class'].includes('Thermostat')) {
              let marker = await TextMarker(equipmentLayer, { name: 'T' });
              if (marker && marker.options) {
                marker.options.pane = 'pane-labels';
              }
              markers.push(marker);
            } else if (!equipmentRecord['@class'].includes('Plenum')) {
              const layers = equipmentLayer.getLayers ? equipmentLayer.getLayers() : [equipmentLayer];
              for (const layer of layers) {
                let marker = await FittedTextMarker(layer, equipmentRecord);
                if (marker && marker.options) {
                  marker.options.pane = 'pane-labels';
                }
                markers.push(marker);
              }
            }
          }
          // Add markers after a brief delay
          markers = markers.filter(Boolean);
          if (markers.length) {
            setTimeout(() => {
              markers.forEach(marker => {
                marker.UID = feature.UID;
                marker['@class'] = equipmentRecord['@class'];
                ctrl.allLayers.push(marker);
                ctrl.equipmentParentLayer.addLayer(marker);
              });
            }, 1000);
          }
        }
        catch (e) {
          console.log(equipmentLayer, equipmentRecord);
          console.error('Error adding text label', e);
        }

        // Prepare for quickview popup binding depending on edit mode
        equipmentLayer._quickviewRecord = equipmentRecord;
        equipmentLayer.isPopupBound = false;
        equipmentLayer.UID = feature.UID;
        bindOrUnbindEquipmentPopup(equipmentLayer);
      }
    };

    const handleFeatureRemovals = function handleFeatureRemovals (features) {
      features.forEach(featureUid => {
        try {
          let featureLayers = ctrl.allLayers.filter(l => l.UID == featureUid);
          
          if (!featureLayers?.length) return;
          featureLayers.forEach(featureLayer => {
            if (ctrl.equipmentParentLayer.hasLayer(featureLayer)) {
              ctrl.equipmentParentLayer.removeLayer(featureLayer);
              let allLayersIndex = ctrl.allLayers.indexOf(featureLayer);
              if (allLayersIndex > -1) ctrl.allLayers.splice(allLayersIndex, 1);
            }
          });
        } catch (e) {
          console.error('Error removing feature', e);
        }
      });
    }

    ctrl.eventBus.on('mithril:oncreate', () => {
      ctrl.equipmentParentLayer = new L.FeatureGroup([], { pane: 'pane-equipment' });
      ctrl.map.addLayer(ctrl.equipmentParentLayer);
      ctrl.equipmentParentLayer.bringToFront();
      // Ensure popup bindings reflect current edit mode on create
      updateAllEquipmentPopupBindings();
    });

    ctrl.eventBus.on('mithril:onupdate', () => {
      ctrl.equipmentParentLayer.bringToFront();
      updateAllEquipmentPopupBindings();
    });

    // Reconcile bindings when edit mode toggles
    const removeActiveEquipmentLayers = () => {
      if (!ctrl?.equipmentRecord || !ctrl.equipmentRecord() || !ctrl.equipmentParentLayer) return;
      const activeRid = ctrl.equipmentRecord()['@rid'];
      const activeUid = typeof ctrl.getFeatureUid === 'function' ? ctrl.getFeatureUid(ctrl.equipmentRecord()) : null;
      try {
        const layersToRemove = ctrl.equipmentParentLayer.getLayers().filter(l => {
          const matchesRid = l?._quickviewRecord?.['@rid'] === activeRid;
          const matchesUid = activeUid && l?.UID === activeUid;
          return matchesRid || matchesUid;
        });
        layersToRemove.forEach(layer => {
          ctrl.equipmentParentLayer.removeLayer(layer);
          const idx = ctrl.allLayers.indexOf(layer);
          if (idx > -1) ctrl.allLayers.splice(idx, 1);
        });
      } catch (_) {}
    };

    edit.on('toggle', () => {
      updateAllEquipmentPopupBindings();
      if (edit.vm.editing()) removeActiveEquipmentLayers();
    });

    ctrl.eventBus.on('featuresToAdd', handleFeatureAdditions);
    ctrl.eventBus.on('featuresToRemove', handleFeatureRemovals);

    function bindOrUnbindEquipmentPopup(layer) {
      if (!layer || !layer.bindPopup || !layer.unbindPopup) return;
      // If editing, ensure popups are disabled
      if (edit.vm.editing()) {
        if (layer.isPopupBound) {
          try { layer.unbindPopup(); } catch (e) {}
          layer.isPopupBound = false;
        }
        return;
      }
      // Not editing: bind popup if not already bound
      if (!layer.isPopupBound) {
        try {
          const popup = QuickviewPopup(layer, layer._quickviewRecord, ctrl.map);
          popup.UID = layer.UID;
          layer.bindPopup(popup, { autoClose: false, closeOnClick: false, interactive: false });
          layer.isPopupBound = true;
        } catch (e) {
          // Binding can fail for some layer types; ignore silently
        }
      }
    }

    function updateAllEquipmentPopupBindings() {
      if (!ctrl?.equipmentParentLayer) return;
      try {
        ctrl.equipmentParentLayer.getLayers().forEach(bindOrUnbindEquipmentPopup);
      } catch (e) {}
    }
  }
}

module.exports = EquipmentLayerPlugin;
